// Click-through tours of real app screens for marketing pages (components/walkthrough/ScreenTour.vue).
// Every screen is a real render from the app's visual checks with sample data (release-notes rules in
// docs/release-notes.md), never a mockup. Each step has a desktop screen and, where one exists, a phone
// render; without one the phone shows the desktop screen. Captions only state what the screen shows and
// what the release notes say the feature does; AI Import wording follows AGENTS.md (CSV or Excel exports,
// AI maps the columns, you check every row; no one-click importer).

export interface ScreenTourShot {
  src: string
  width: number
  height: number
  alt: string
}

export interface ScreenTourStep {
  id: string
  /** Short label for the step list. */
  label: string
  caption: string
  subCaption: string
  desktop: ScreenTourShot
  phone?: ScreenTourShot
}

export interface ScreenTour {
  slug: string
  steps: ScreenTourStep[]
}

const rn = '/images/release-notes'

export const switchFromHoneyBookTour: ScreenTour = {
  slug: 'switch-from-honeybook',
  steps: [
    {
      id: 'import',
      label: 'Bring your data',
      caption: 'Upload your HoneyBook export and let AI Import read it',
      subCaption: 'Upload the CSV or Excel export, check the source app AI Import suggests, then review every row before anything is saved.',
      desktop: {
        src: `${rn}/2026-10-23-review-requests-and-imports/import-sources.webp`,
        width: 1600,
        height: 840,
        alt: 'The AI Import step Confirm what we detected on desktop, with the Source app list open on HoneyBook, Dubsado, Bonsai, 17hats, Moxie and GoHighLevel.',
      },
      phone: {
        src: `${rn}/2026-10-23-review-requests-and-imports/import-sources-phone.webp`,
        width: 780,
        height: 970,
        alt: 'The same Confirm what we detected step on a phone with the Source app list open on HoneyBook, Dubsado, Bonsai, 17hats, Moxie and GoHighLevel.',
      },
    },
    {
      id: 'proposal',
      label: 'Send a proposal',
      caption: 'Offer good, better and best, and let your client pick',
      subCaption: 'Group options into a package and mark the default; your client picks the one they want. Cover images and page backgrounds are on Specialist and up.',
      desktop: {
        src: `${rn}/2026-10-26-proposal-packages-and-write-with-ai/proposal-packages.webp`,
        width: 1600,
        height: 1200,
        alt: 'The proposal editor on desktop: Scope & investment with Website: Starter, Growth and Signature as options in a Website package, Growth tagged as the default, and a total of $8,500.00.',
      },
      phone: {
        src: `${rn}/2026-10-26-proposal-packages-and-write-with-ai/proposal-packages-phone.webp`,
        width: 780,
        height: 1280,
        alt: "The client's proposal page on a phone: Website: Starter, Growth and Signature as a Choose one group with radio buttons, and Signature selected.",
      },
    },
    {
      id: 'contract',
      label: 'Get it signed',
      caption: 'Send the contract for e-signature and see who has signed',
      subCaption: 'Signers sign in order, and you can see who has signed and who you are still waiting on.',
      desktop: {
        src: `${rn}/2026-09-20-contracts-and-e-signature/contract-detail.webp`,
        width: 1600,
        height: 1000,
        alt: 'A contract waiting on a countersignature: one of two signers has signed, with the signing order, reminders every 3 days, email code verification and the activity timeline beside the document.',
      },
      phone: {
        src: `${rn}/2026-09-20-contracts-and-e-signature/contract-detail-phone.webp`,
        width: 780,
        height: 1688,
        alt: 'A Website redesign master services agreement on a phone: waiting on you to countersign, with one of two signers done in the signing order.',
      },
    },
    {
      id: 'invoice',
      label: 'Invoice the work',
      caption: 'Turn the work into an invoice',
      subCaption: 'Add line items with hours and rates and apply a discount; the total adds it all up.',
      // Rendered by the app's test/visual/invoices/editor_visual_check.dart (sample data), cropped to the Details panel.
      desktop: {
        src: '/images/tours/invoice-editor-items.webp',
        width: 1440,
        height: 900,
        alt: 'The invoice editor on desktop: three line items (Brand strategy workshop, UI design sprint at 40 hours, Design system documentation), a 5% discount and a total of $9,690.00.',
      },
      phone: {
        src: '/images/tours/invoice-editor-items-phone.webp',
        width: 585,
        height: 1266,
        alt: 'A new invoice on a phone with the same three items, a 5% discount and a $9,690.00 total.',
      },
    },
    {
      id: 'portal',
      label: 'Share a portal',
      caption: 'Set the layout every client portal starts with',
      subCaption: 'Choose a page layout for your clients\' portals; the preview updates as you change things.',
      // Top of the release-note render (portal-per-client.webp): title, tabs and the page layouts.
      desktop: {
        src: '/images/tours/client-portal-settings.webp',
        width: 1070,
        height: 880,
        alt: 'Client portal settings on the Clients tab: every client portal starts with these, with the Single column, Two columns and Tabs page layouts.',
      },
      // Top-left crop of the same render, so the text stays readable on a phone.
      phone: {
        src: '/images/tours/client-portal-settings-phone.webp',
        width: 700,
        height: 875,
        alt: 'Client portal settings on the Clients tab, with the Single column and Two columns page layouts.',
      },
    },
  ],
}

// /pricing: the same five screens; the first step names every tool AI Import detects, not only HoneyBook.
// /pricing is natively localized, so components/PricingTour.vue repeats every string below as a tr() literal
// (tests/ui/pricing-tour.test.mjs keeps the two in step).
export const everyPlanTour: ScreenTour = {
  slug: 'every-plan',
  steps: switchFromHoneyBookTour.steps.map((step) =>
    step.id === 'import'
      ? {
          ...step,
          caption: 'Bring your clients and history with AI Import',
          subCaption: 'Upload a CSV or Excel export from HoneyBook, Dubsado, Bonsai, 17hats, Moxie or a spreadsheet, check the source app AI Import suggests, then review every row before anything is saved.',
        }
      : step,
  ),
}
